import { OptimizedImage } from "@/components/optimized-image";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Users,
  Shield,
  CheckCircle,
  ArrowRight,
  HandshakeIcon,
  Megaphone,
  HeartPulse,
  Newspaper,
  Calendar,
  Lock,
} from "lucide-react";
import { UpcomingEventsWidget } from "@/components/upcoming-events-widget";
import { MtcIntroduction } from "@/components/mtc-introduction";
import type { Article } from "@shared/schema";

function formatNewsDate(date: string | Date) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function LatestNewsSection() {
  const { data: articles } = useQuery<Article[]>({
    queryKey: ["/api/articles", "?category=news"],
  });

  const latest = (articles ?? []).slice(0, 3);
  if (latest.length === 0) return null;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">Latest News</h2>
            <p className="mt-2 text-lg text-muted-foreground">
              The latest updates from the Michigan Trauma Coalition.
            </p>
          </div>
          <Link href="/about/news">
            <Button variant="outline" className="gap-2" data-testid="button-all-news">
              All News
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-news-grid">
          {latest.map((article) => (
            <Link key={article.id} href={`/about/news/${article.slug}`}>
              <Card
                className="cursor-pointer overflow-visible hover-elevate h-full"
                data-testid={`card-home-news-${article.id}`}
              >
                {article.featuredImageUrl ? (
                  <div className="aspect-video w-full overflow-hidden rounded-t-md bg-muted">
                    <OptimizedImage
                      maxWidth={960}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      src={article.featuredImageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full overflow-hidden rounded-t-md bg-muted flex items-center justify-center">
                    <Newspaper className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                )}
                <CardContent className="p-4 space-y-2">
                  {article.isMembersOnly && (
                    <Badge variant="outline" className="text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      Members Only
                    </Badge>
                  )}
                  <h3 className="font-semibold line-clamp-2">{article.title}</h3>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{formatNewsDate(article.publishedAt || article.createdAt!)}</span>
                  </div>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-3">{article.excerpt}</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const benefits = [
  {
    title: "Strengthen Your Trauma Program",
    icon: Building2,
    points: [
      "Access a statewide network of experienced trauma leaders and subject matter experts",
      "Share best practices, innovative solutions, and lessons learned from trauma centers across Michigan",
      "Stay informed on emerging trauma care standards, verification requirements, and performance improvement strategies",
    ],
  },
  {
    title: "Empower Your Team",
    icon: Users,
    points: [
      "Unlimited participation for all hospital staff in MTC committees, workgroups, educational programs, and initiatives",
      "Exclusive member discounts on educational courses, certification exams, conferences, and professional development opportunities",
      "Scholarship opportunities to support specialty certifications, leadership development, and continuing education",
    ],
  },
  {
    title: "Gain a Voice in Michigan Trauma Care",
    icon: Megaphone,
    points: [
      "Voting rights for Member Hospitals at membership meetings",
      "Opportunities to influence statewide trauma initiatives, advocacy efforts, and policy discussions",
      "Representation in shaping the future of Michigan's trauma system",
    ],
  },
  {
    title: "Build Connections That Matter",
    icon: HandshakeIcon,
    points: [
      "Networking opportunities with trauma professionals from every level of care throughout Michigan",
      "Mentoring relationships for new and experienced trauma leaders",
      "Leadership opportunities through committees, councils, task forces, and the Executive Board",
    ],
  },
  {
    title: "Advance Excellence and Readiness",
    icon: Shield,
    points: [
      "Collaborate with peers to improve patient outcomes and trauma system performance",
      "Access resources and expertise to support trauma center development, verification, and ongoing excellence",
      "Participate in statewide efforts that strengthen trauma care for all Michigan residents",
    ],
  },
  {
    title: "Promote Safer Communities",
    icon: HeartPulse,
    points: [
      "Support community education through initiatives such as Stop the Bleed and fall prevention",
      "Explore injury prevention resources, educational materials, and program toolkits",
      "Participate in MTC injury prevention committees and subcommittees to help reduce preventable injuries across Michigan",
    ],
  },
];

export default function LandingPage() {

  return (
    <div>
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[hsl(var(--primary))] text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))] via-[hsl(212,96%,18%)] to-[hsl(var(--primary))]" />
        <div className="relative mx-auto max-w-4xl text-center">
          <div className="space-y-6">
            <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl text-primary-foreground">
              Michigan Trauma Coalition
            </h1>

            <p className="text-lg text-primary-foreground/80 leading-relaxed max-w-2xl mx-auto">
              The only statewide multi-professional organization dedicated exclusively 
              to the advancement of trauma care, education, research, and advocacy 
              across the trauma continuum since 1991.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/membership">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto gap-2" data-testid="button-hero-cta">
                  Apply for Membership
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="/about/mission">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10 backdrop-blur"
                  data-testid="button-learn-more"
                >
                  Learn About MTC
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-6 justify-center flex-wrap">
              <div className="flex items-center gap-2 text-sm text-primary-foreground/80">
                <CheckCircle className="h-4 w-4" />
                <span>Established 1991</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/80">
                <Shield className="h-4 w-4" />
                <span>State Partner</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/80">
                <Building2 className="h-4 w-4" />
                <span>100+ Member Hospitals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MtcIntroduction />

      <LatestNewsSection />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12">
            <div className="flex flex-col items-center gap-6 text-center">
              <div className="max-w-3xl">
                <h2 className="text-3xl font-bold leading-tight sm:text-4xl">
                  Why Join the MTC?
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                  Join the Michigan Trauma Coalition and connect your team to the expertise, resources, advocacy, and relationships that strengthen trauma care across Michigan.
                </p>
              </div>
              <Link href="/membership">
                <Button size="lg" className="gap-2" data-testid="button-why-join-cta">
                  Join MTC Today
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <div className="grid w-full max-w-lg grid-cols-2 gap-6 border-t pt-6">
                <div>
                  <div className="text-3xl font-bold text-primary sm:text-4xl">100+</div>
                  <div className="mt-1 text-sm text-muted-foreground">Member Hospitals</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary sm:text-4xl">1991</div>
                  <div className="mt-1 text-sm text-muted-foreground">Serving MI Since</div>
                </div>
              </div>
            </div>
            <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label="Membership benefits">
              {benefits.map(({ title, points, icon: BenefitIcon }) => (
                <li key={title} className="h-full">
                  <Card className="h-full hover-elevate">
                    <CardContent className="flex h-full flex-col items-start gap-4 p-5 sm:p-6">
                      <div className="flex flex-col items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <BenefitIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="text-lg font-semibold leading-snug">{title}</h3>
                      </div>
                      <ul className="list-disc space-y-3 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-primary">
                        {points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <hr className="mx-auto max-w-7xl border-border/70" />
      </div>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">
              Upcoming Events
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-4xl mx-auto">
              Stay connected with educational programs, committee meetings, and conferences.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <UpcomingEventsWidget limit={5} />
          </div>
        </div>
      </section>

    </div>
  );
}
